import { computed, ref, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { defineStore } from 'pinia'

/**
 * RED-236 — "Viimati vaadatud" (recently viewed transactions) on the
 * Tehingute kaart (/transactions).
 *
 * Persistence is client-side only: the list lives in localStorage, namespaced
 * per authenticated user, and survives page refresh and login/logout (Toomas
 * chose variant c) — always keep up to 20; Siim RED-236 comment: localStorage,
 * accept the hard-cache-clear wipe risk, no backend work). There is no server
 * state and no cross-device sync by design.
 *
 * Each entry carries just enough to render a panel row and jump back:
 *   { id, aadress, kpv, tehingusumma, ad_link, ad_link_not_found,
 *     not_vabaturu_tehing, coord }
 * `coord` is the marker's map-projection ([x, y] in EPSG:3301) coordinate,
 * captured from the clicked map feature — the `transaction` row itself has no
 * coordinate columns — and is used for fly-to + the open-marker halo. It may be
 * null for cluster/list opens; row click then falls back to a best-effort pan.
 */

export const MAX_RECENTLY_VIEWED = 20
const STORAGE_PREFIX = 'hindamine.recentlyViewed.v1.'

export const useRecentlyViewedStore = defineStore('recentlyViewed', () => {
  // Newest-first list of viewed transactions (capped at MAX_RECENTLY_VIEWED).
  const items = ref([])
  // Panel expanded/collapsed state — session-only, deliberately not persisted.
  const open = ref(false)

  // Capture the reactive Inertia page once. usePage() is unavailable outside an
  // Inertia app context (e.g. a unit test) — fall back to the shared anon
  // namespace so the store never throws on instantiation.
  let page = null
  try {
    page = usePage()
  } catch {
    page = null
  }

  const currentUserId = () => page?.props?.auth?.user?.id ?? 'anon'
  const storageKey = () => STORAGE_PREFIX + currentUserId()

  const load = () => {
    try {
      const raw = localStorage.getItem(storageKey())
      const parsed = raw ? JSON.parse(raw) : []
      items.value = Array.isArray(parsed)
        ? parsed.slice(0, MAX_RECENTLY_VIEWED)
        : []
    } catch {
      items.value = []
    }
  }

  const persist = () => {
    try {
      localStorage.setItem(storageKey(), JSON.stringify(items.value))
    } catch {
      // Quota exceeded / private mode / storage disabled — the feature simply
      // does not persist; it must never break the map.
    }
  }

  const viewedIds = computed(() => new Set(items.value.map(i => i.id)))
  const count = computed(() => items.value.length)

  /**
   * Record (or re-surface) a viewed transaction. Dedupes by id, moves the
   * entry to the front, and caps the list. A previously captured `coord` is
   * preserved when this open did not supply one (list/cluster re-open).
   */
  const record = item => {
    if (!item || item.id == null) {
      return
    }
    const existing = items.value.find(i => i.id === item.id)
    const next = {
      id: item.id,
      aadress: item.aadress ?? existing?.aadress ?? '',
      kpv: item.kpv ?? existing?.kpv ?? '',
      tehingusumma: item.tehingusumma ?? existing?.tehingusumma ?? null,
      ad_link: item.ad_link ?? existing?.ad_link ?? null,
      ad_link_not_found: item.ad_link_not_found ? 1 : 0,
      not_vabaturu_tehing: item.not_vabaturu_tehing ? 1 : 0,
      coord: item.coord ?? existing?.coord ?? null,
    }
    items.value = [
      next,
      ...items.value.filter(i => i.id !== item.id),
    ].slice(0, MAX_RECENTLY_VIEWED)
  }

  const remove = id => {
    items.value = items.value.filter(i => i.id !== id)
  }

  const clear = () => {
    items.value = []
  }

  // Persist on every mutation. Deep watch because entries are plain objects.
  watch(items, persist, { deep: true })

  // Re-hydrate when the authenticated user changes. Login/logout are Inertia SPA
  // visits (no full page reload), so this singleton Pinia store survives the
  // switch — without this, User B would keep seeing User A's list (and persist
  // it under B's key) on a shared device. Re-reads from B's namespace instead.
  if (page) {
    watch(
      () => page.props?.auth?.user?.id,
      () => load(),
    )
  }

  // Hydrate from localStorage on first instantiation.
  load()

  return {
    items,
    open,
    viewedIds,
    count,
    record,
    remove,
    clear,
    load,
    MAX_RECENTLY_VIEWED,
  }
})
